// eslint-disable-next-line no-restricted-imports
import { Currency, CurrencyAmount, Percent } from '@kodiak-finance/sdk-core'
import HoverInlineText from 'components/HoverInlineText'
import { useMemo } from 'react'

import useTheme from '../../hooks/useTheme'
import { ThemedText } from '../../theme'
import { warningSeverity } from '../../utils/prices'
import { MouseoverTooltip } from '../Tooltip'

function useUSDValue(
  fiatValue: CurrencyAmount<Currency> | null | undefined,
  apiUSDValue?: string
): {
  usdValue: number | null
  source: 'api' | 'fallback' | 'none'
} {
  return useMemo(() => {
    if (apiUSDValue) {
      const apiValue = parseFloat(apiUSDValue)
      if (!isNaN(apiValue) && apiValue > 0) {
        return {
          usdValue: apiValue,
          source: 'api' as const,
        }
      }
    }

    if (fiatValue) {
      const fallbackValue = parseFloat(fiatValue.toFixed(6))
      if (!isNaN(fallbackValue) && fallbackValue > 0) {
        return {
          usdValue: fallbackValue,
          source: 'fallback' as const,
        }
      }
    }

    return {
      usdValue: null,
      source: 'none' as const,
    }
  }, [apiUSDValue, fiatValue])
}

export function FiatValue({
  fiatValue,
  priceImpact,
  apiUSDValue,
}: {
  fiatValue: CurrencyAmount<Currency> | null | undefined
  priceImpact?: Percent
  apiUSDValue?: string
}) {
  const theme = useTheme()
  const { usdValue } = useUSDValue(fiatValue, apiUSDValue)
  const priceImpactColor = useMemo(() => {
    if (!priceImpact) return undefined
    if (priceImpact.greaterThan('0')) {
      return theme.deprecated_green1
    }
    if (priceImpact.lessThan('0')) {
      const severity = warningSeverity(priceImpact)
      if (severity < 1) return theme.deprecated_text3
      if (severity < 3) return theme.deprecated_yellow1
      return theme.deprecated_red1
    }

    // Zero impact
    return theme.deprecated_text3
  }, [priceImpact, theme.deprecated_green1, theme.deprecated_red1, theme.deprecated_text3, theme.deprecated_yellow1])

  const formatUSDValue = (value: number): string => {
    if (isNaN(value) || value <= 0) return '0'

    let formattedValue: string

    if (value > 0 && value < 0.000001) {
      // Very small amounts - use significant figures (but this is rare for USD)
      formattedValue = value.toPrecision(4)
    } else if (value >= 0.000001 && value < 0.01) {
      // Small amounts - high precision
      formattedValue = value.toFixed(6)
    } else if (value >= 0.01 && value < 1000) {
      // Medium amounts - moderate precision
      formattedValue = value.toFixed(2)
    } else if (value >= 1000) {
      // Large amounts - less precision
      formattedValue = value.toFixed(2)
    } else {
      // Fallback
      formattedValue = value.toPrecision(6)
    }

    const [integerPart, decimalPart] = formattedValue.split('.')
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

    return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger
  }

  return (
    <ThemedText.DeprecatedBody fontSize={12} color={theme.textSecondary}>
      {usdValue && (
        <>
          $
          <HoverInlineText
            text={formatUSDValue(usdValue)}
            textColor={usdValue ? theme.deprecated_text3 : theme.deprecated_text4}
          />
        </>
      )}
      {priceImpact ? (
        <span style={{ color: priceImpactColor }}>
          &nbsp;
          <MouseoverTooltip text="The estimated difference between the USD values of input and output amounts.">
            ({priceImpact.greaterThan('0') ? '+' : ''}
            {priceImpact.toSignificant(3)}%)
          </MouseoverTooltip>
        </span>
      ) : null}
    </ThemedText.DeprecatedBody>
  )
}
